import React, { useState } from "react";
import { login } from "../../api/auth";
import { startGoogleSignIn, startAppleSignIn } from "../../api/socialAuth";
import logoUrl from "../../../assets/chlk-script-logo-varsity-blue.svg";

interface Props {
  onSuccess: () => void;
}

type LoadingState = null | "email" | "google" | "apple";

export default function Login({ onSuccess }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState<LoadingState>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading("email");
    try {
      await login(email, password);
      onSuccess();
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        setError("Invalid email or password.");
      } else if (status === 429) {
        setError("Too many attempts. Please wait and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(null);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    setLoading("google");
    try {
      const result = await startGoogleSignIn();
      if (result.success) {
        onSuccess();
      } else if (result.error) {
        setError(result.error);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(null);
    }
  }

  async function handleAppleSignIn() {
    setError(null);
    setLoading("apple");
    try {
      const result = await startAppleSignIn();
      if (result.success) {
        onSuccess();
      } else if (result.error) {
        setError(result.error);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(null);
    }
  }

  const isLoading = loading !== null;

  return (
    <div className="login">
      <img className="login-logo" src={logoUrl} alt="CHLK" />
      <p className="login-tagline">
        Insert CHLK football plays into PowerPoint for scout cards, playbook creation, and installs.
        Sign in with your CHLK account to access your synced play library.
      </p>
      
      <div className="login-social">
        <button
          type="button"
          className="btn-social btn-google"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          <svg className="social-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loading === "google" ? "Signing in…" : "Continue with Google"}
        </button>
        
        <button
          type="button"
          className="btn-social btn-apple"
          onClick={handleAppleSignIn}
          disabled={isLoading}
        >
          <svg className="social-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          {loading === "apple" ? "Signing in…" : "Continue with Apple"}
        </button>
      </div>

      <div className="login-divider">
        <span>or</span>
      </div>

      <form onSubmit={handleSubmit} className="login-form">
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={isLoading}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            disabled={isLoading}
          />
        </label>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" disabled={isLoading}>
          {loading === "email" ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
